import React, { useContext } from "react";
import { useLocation } from "react-router-dom";
import { NoteState } from "../context/notes/NoteState";
import {
  getCachedMenu,
  isCatalogPath,
  isMenuAccessPending,
  isStaffAssessmentPath,
  pathAllowed,
} from "../utils/menuAccessCache";
import { legacyAllowsPath } from "../utils/legacyRouteAccess";
import AccessDenied from "./Includes/AccessDenied";

/**
 * Enforces dynamic menu access when cache is warm.
 * Menu grants/revokes win over hardcoded legacyAllowed,
 * except hard-coded Sales/Cafe POS paths (legacyAllowsPath).
 * Denied access shows AccessDenied (not a silent home redirect).
 */
export default function MenuPathGate({ children, legacyAllowed }) {
  const { state } = useContext(NoteState);
  const { role } = state;
  const pathname = useLocation().pathname;
  const roleStr = String(role ?? "");

  if (roleStr === "1" && pathname.startsWith("/admin/role-access")) {
    return children;
  }

  const decision = resolveAccess(roleStr, pathname, legacyAllowed);
  if (decision === "deny") {
    return <AccessDenied />;
  }

  return children;
}

export function resolveAccess(roleStr, pathname, legacyAllowed) {
  if (isStaffAssessmentPath(pathname)) return "allow";

  const path = String(pathname ?? "");
  if (path === "/mis-report" || path.startsWith("/mis-report/")) {
    return roleStr === "1" ? "allow" : "deny";
  }

  const cached = getCachedMenu(roleStr);
  const allLinks = cached?.all_link_paths || [];
  const allowed = cached?.allowed_paths || [];

  if (isMenuAccessPending(roleStr)) {
    return "allow";
  }

  // Once the menu catalog is loaded, Role & Menu Access wins over hardcoded lists.
  if (allLinks.length && isCatalogPath(allLinks, pathname)) {
    return pathAllowed(allowed, pathname) ? "allow" : "deny";
  }

  if (legacyAllowsPath(roleStr, pathname)) {
    return "allow";
  }

  if (Array.isArray(legacyAllowed) && legacyAllowed.length > 0) {
    return legacyAllowed.map(String).includes(String(roleStr))
      ? "allow"
      : "deny";
  }

  return "allow";
}